import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import Img from "./Img";

// Become registration closes end-of-day August 22, 2026 — the popup retires
// itself after that, no deploy needed.
const DEADLINE = new Date(2026, 7, 22, 23, 59, 59);
const DISMISS_KEY = "become-popup-2026";

/** Site-wide Become promo: shows once per visit until the deadline passes. */
const BecomePopup = () => {
  const [open, setOpen] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (new Date() > DEADLINE) return;
    if (sessionStorage.getItem(DISMISS_KEY)) return;
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Become — 6 month post-trip coaching program"
      onClick={dismiss}
    >
      <div
        className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-md border border-hxp-sand-dark bg-hxp-cream shadow-2xl md:flex-row"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeRef}
          type="button"
          onClick={dismiss}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 rounded-full border border-hxp-sand-dark bg-hxp-cream/90 p-1.5 text-foreground/70 transition-colors hover:bg-hxp-cream hover:text-foreground"
        >
          <X size={16} />
        </button>

        {/* The poster carries the brand — BECOME, Love God. Love People. */}
        <div className="hidden bg-[#e3e1d3] md:block md:w-1/2">
          <Img
            sizes="(min-width: 768px) 400px, 1px"
            src="/images/become-poster.png"
            alt="Become — Love God. Love People. HXP"
            className="h-full w-full object-cover"
          />
        </div>
        <Img
          sizes="(min-width: 768px) 1px, 100vw"
          src="/images/become-poster.png"
          alt="Become — Love God. Love People. HXP"
          className="max-h-56 w-full bg-[#e3e1d3] object-contain md:hidden"
        />

        <div className="flex flex-col justify-center p-7 text-center md:w-1/2 md:p-10 md:text-left">
          <div className="btn-caps text-xs tracking-[0.16em] text-primary">
            Final Call
          </div>
          <h2 className="font-heading mt-3 text-3xl leading-tight md:text-4xl">
            6-month post-trip coaching program
          </h2>
          <p className="font-body mt-4 text-sm leading-relaxed text-foreground/75">
            The trip doesn’t have to be the peak, it can be the start. Keep
            the momentum going with the support you need.
          </p>
          <p className="btn-caps mt-5 text-sm tracking-[0.12em] text-hxp-red-dark">
            Last day to register: August 22
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center md:justify-start">
            <a
              href="https://become.hxp.org"
              target="_self"
              className="btn-caps inline-flex items-center justify-center rounded-sm bg-hxp-red-dark px-7 py-3 text-sm text-white transition-colors hover:bg-[#9d382e]"
            >
              Register Now
            </a>
            <button
              type="button"
              onClick={dismiss}
              className="btn-caps inline-flex items-center justify-center rounded-sm border border-hxp-sand-dark px-7 py-3 text-sm text-foreground/70 transition-colors hover:bg-hxp-cream-2"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BecomePopup;
